<script setup lang="ts">
import type { Content } from '@prismicio/client'
import { trackWizardEvent } from '~/utils/wizardAnalytics'

const { pkg, fieldErrors, persist, setRecommendedPackage } = useOfferteWizard()
const { client } = usePrismic()

const { data: packages } = await useAsyncData('wizard-packages', () =>
  client.getAllByType<Content.AircoPackageDocument>('airco_package', {
    orderings: [{ field: 'my.airco_package.from_price' }],
  }),
)

watchEffect(() => {
  setRecommendedPackage(packages.value ?? null)
})

const priceFormatter = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
})

const formatPrice = (v: number | null | undefined) =>
  typeof v === 'number' ? priceFormatter.format(v) : ''

function selectPackage(uid: string) {
  const isNew = pkg.value !== uid
  pkg.value = uid
  if (fieldErrors.value._pkg) fieldErrors.value = {}
  persist()
  if (isNew) {
    trackWizardEvent('wizard_package_select', { package: uid })
  }
}
</script>

<template>
  <section class="pa-wizard__panel">
    <div class="pa-wizard__panel-head">
      <h2>Kies je pakket</h2>
      <p>Welke oplossing past het beste bij jouw woning? Twijfel je? Kies dan voor persoonlijk advies — dan denken we met je mee.</p>
    </div>

    <p v-if="fieldErrors._pkg" class="pa-wizard__error pa-wizard__error--block">
      {{ fieldErrors._pkg }}
    </p>

    <div class="pa-wizard__pick-grid">
      <button
          v-for="p in packages"
          :key="p.id"
          type="button"
          class="pa-wizard__pick"
          :class="{ 'pa-wizard__pick--selected': pkg === p.uid }"
          @click="selectPackage(p.uid)"
      >
        <span v-if="p.data.is_popular" class="pa-wizard__pick-ribbon">Meest gekozen</span>
        <span class="pa-wizard__pick-check" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </span>
        <h3>{{ p.data.title }}</h3>
        <div v-if="p.data.subtitle" class="pa-wizard__pick-area">{{ p.data.subtitle }}</div>
        <div v-if="typeof p.data.from_price === 'number'" class="pa-wizard__pick-price">
          {{ formatPrice(p.data.from_price) }} <small>vanaf</small>
        </div>
        <ul v-if="p.data.usps?.length" class="pa-wizard__pick-usps">
          <li v-for="(row, i) in p.data.usps" :key="i">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            <span>{{ row.usp }}</span>
          </li>
        </ul>
      </button>

      <button
          type="button"
          class="pa-wizard__pick-advice"
          :class="{ 'pa-wizard__pick-advice--selected': pkg === 'advice' }"
          @click="selectPackage('advice')"
      >
        <span class="pa-wizard__pick-advice-icon" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 3a6 6 0 0 0-6 6c0 2.5 1.5 3.8 2.5 5 .7.8 1 1.5 1 2.5h5c0-1 .3-1.7 1-2.5 1-1.2 2.5-2.5 2.5-5a6 6 0 0 0-6-6z" />
            <line x1="9.5" y1="21" x2="14.5" y2="21" />
          </svg>
        </span>
        <span class="pa-wizard__pick-advice-text">
          <span class="pa-wizard__pick-advice-title">Ik weet het nog niet — adviseer mij</span>
          <span class="pa-wizard__pick-advice-sub">Onze specialist denkt met je mee op basis van je woning</span>
        </span>
        <span class="pa-wizard__pick-check" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </span>
      </button>
    </div>
  </section>
</template>
